import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import "./horizonTable.css";
import { toast } from "react-toastify";
import taylorTechLogo from "../../assets/Rectangle 6.png";
import onsiteproLogo from "../../assets/logo2 2.png";

const MAX_QTY = 200;
const pageSize = 10;

function buildQrPayload(labelCode) {
  const base = String(process.env.REACT_APP_ASSET_QR_SCAN_BASE_URL || "").replace(/\/+$/, "");
  if (base) return `${base}/${encodeURIComponent(labelCode)}`;
  return labelCode;
}

function formatAssetQrPdfDateTime() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
}

function LabelQrThumb({ labelCode }) {
  const [dataUrl, setDataUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!labelCode) {
      setDataUrl("");
      return undefined;
    }
    const payload = buildQrPayload(labelCode);
    QRCode.toDataURL(payload, { width: 72, margin: 1, errorCorrectionLevel: "M" })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDataUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [labelCode]);
  if (!dataUrl) {
    return <div style={{ width: 72, height: 72, background: "#f7fafc", borderRadius: 8 }} />;
  }
  return (
    <img
      src={dataUrl}
      width={50}
      height={50}
      alt=""
      style={{ borderRadius: 8, display: "block", border: "1px solid #e2e8f0" }}
    />
  );
}

function formatCreatedAt(val) {
  if (!val) return "-";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  } catch {
    return "-";
  }
}

function loadImageDataUrl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = src;
  });
}

function addHseqStyleHeader(doc, pageWidth, logoDataUrl) {
  const headerHeight = 56;
  doc.setFillColor(139, 22, 52);
  doc.setDrawColor(139, 22, 52);
  doc.rect(0, 0, pageWidth, headerHeight, "FD");
  try {
    const logoWidth = 88;
    const logoHeight = 30;
    const logoX = 14;
    const logoY = (headerHeight - logoHeight) / 2;
    doc.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight, "", "NONE");
  } catch {}
  const titleX = 112;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text("Asset QR Label", titleX, 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 40);
  return headerHeight;
}

function AssetQrLabelsPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [newBatchQuantity, setNewBatchQuantity] = useState("");
  const [labels, setLabels] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentRole = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return String(user?.role || "").toLowerCase();
    } catch {
      return "";
    }
  })();
  const canCreateLabels = currentRole === "admin" || currentRole === "subadmin";

  useEffect(() => {
    fetchLabels();
  }, []);

  function closeAddModal() {
    setShowAddModal(false);
    setNewBatchQuantity("");
  }

  function handleQuantityChange(e) {
    const digitsOnly = String(e.target.value || "").replace(/\D/g, "").slice(0, String(MAX_QTY).length);
    setNewBatchQuantity(digitsOnly);
  }

  async function fetchLabels() {
    setLoading(true);
    setError(null);
    try {
      const rawApi = process.env.REACT_APP_API_URL;
      const baseUrl = String(rawApi || "").replace(/\/+$/, "");
      const token = localStorage.getItem("token");
      if (!token) {
        setLabels([]);
        setError("Authentication required. Please login again.");
        return;
      }
      if (!baseUrl) {
        setLabels([]);
        setError(null);
        return;
      }
      const res = await fetch(`${baseUrl}/api/asset-management/labels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = null;
      try {
        const text = await res.text();
        if (text) data = JSON.parse(text);
      } catch {
        data = null;
      }
      if (!res.ok) {
        setLabels([]);
        if (res.status === 401) setError("Authentication failed. Please login again.");
        else if (res.status === 403) setError("You don't have permission to access this data.");
        else if (data && typeof data.message === "string" && data.message) setError(data.message);
        else setError(`Failed to load labels. Server responded with ${res.status}.`);
        return;
      }
      if (Array.isArray(data?.data)) {
        setLabels(data.data);
        setError(null);
        return;
      }
      setLabels([]);
      setError(null);
    } catch (err) {
      console.error("Error fetching labels:", err);
      setLabels([]);
      setError(null);
    } finally {
      setLoading(false);
    }
  }

  const handlePrintLabels = async () => {
    const n = Math.floor(Number(String(newBatchQuantity).trim()));
    if (!Number.isFinite(n) || n < 1 || n > MAX_QTY) {
      toast.error(`Quantity must be between 1 and ${MAX_QTY}.`);
      return;
    }
    const addButton = document.querySelector(".modal-footer button[type='button']:last-child");
    if (addButton) {
      addButton.disabled = true;
      addButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Creating...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/asset-management/labels/print", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quantity: n, format: "zebra" }),
      });
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (res.ok && ct.includes("application/pdf")) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        let filename = `assetQr_${formatAssetQrPdfDateTime()}.pdf`;
        const cd = res.headers.get("content-disposition");
        if (cd) {
          const m = /filename\*?=(?:UTF-8''|"?)([^";\n]+)/i.exec(cd);
          if (m) filename = decodeURIComponent(m[1].replace(/"/g, "").trim());
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`${n} labels created. PDF downloaded.`);
        fetchLabels();
        closeAddModal();
      } else if (res.ok) {
        const data = await res.json().catch(() => null);
        toast.success(data?.message || `${n} labels created successfully.`);
        fetchLabels();
        closeAddModal();
      } else {
        let msg = "Failed to create or print labels.";
        try {
          const data = await res.json();
          if (data?.message) msg = data.message;
        } catch {
          msg = res.statusText || msg;
        }
        toast.error(msg);
      }
    } catch {
      toast.error("Failed to create labels");
    } finally {
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = "Create Labels";
      }
    }
  };

  const handleDownloadPdf = async (row) => {
    try {
      const payload = buildQrPayload(row.labelCode);
      const qrDataUrl = await QRCode.toDataURL(payload, { width: 500, margin: 1, errorCorrectionLevel: "M" });
      const logoDataUrl = await loadImageDataUrl(taylorTechLogo);
      const onsiteLogoDataUrl = await loadImageDataUrl(onsiteproLogo);
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const headerHeight = addHseqStyleHeader(doc, pageWidth, logoDataUrl);

      doc.setTextColor(35, 35, 35);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Label Code:", 36, headerHeight + 28);
      doc.setFont("helvetica", "normal");
      doc.text(row.labelCode || "-", 112, headerHeight + 28);

      doc.setDrawColor(220, 220, 220);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(145, headerHeight + 64, 305, 230, 12, 12, "FD");

      const size = 150;
      const x = (pageWidth - size) / 2;
      const y = headerHeight + 112;
      try {
        const props = doc.getImageProperties(onsiteLogoDataUrl);
        const brandW = 150;
        const brandH = Math.max(28, (brandW * props.height) / props.width);
        const brandX = pageWidth / 2 - brandW / 2;
        const brandY = y - 44;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(brandX - 8, brandY - 4, brandW + 16, brandH + 8, 8, 8, "F");
        doc.addImage(onsiteLogoDataUrl, "PNG", brandX, brandY, brandW, brandH, "", "NONE");
      } catch {}
      doc.addImage(qrDataUrl, "PNG", x, y, size, size);
      const safeCode = String(row.labelCode || "label").replace(/[\\/:"*?<>|]+/g, "-");
      doc.save(`assetQr_${safeCode}_${formatAssetQrPdfDateTime()}.pdf`);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const deleteButton = document.querySelector(".modal-footer button.btn-danger");
    if (deleteButton) {
      deleteButton.disabled = true;
      deleteButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Deleting...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/asset-management/labels/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchLabels();
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      if (deleteButton) {
        deleteButton.disabled = false;
        deleteButton.innerHTML = "Delete";
      }
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const filtered = labels.filter((row) => {
    if (row.status !== "unassigned") return false;
    const fields = [
      row.labelCode,
      row.status,
      formatCreatedAt(row.createdAt),
    ].map((val) => (val || "").toString().toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every((word) => fields.some((field) => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered
    .slice()
    .reverse()
    .slice((page - 1) * pageSize, page * pageSize);
  const emptyRowsCount = Math.max(0, pageSize - paged.length);

  return (
    <>
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
            <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>QR Labels</h2>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <i
                  className="fa fa-search"
                  style={{ position: "absolute", left: "12px", color: "#8b1634", fontSize: "14px" }}
                />
                <input
                  type="text"
                  placeholder="Type to Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    padding: "10px 12px 10px 36px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    fontSize: "14px",
                    outline: "none",
                    width: "250px",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b1634")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>
              {search.trim() ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "12px",
                    border: "none",
                    background: "#e53e3e",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Reset
                </button>
              ) : null}
              {canCreateLabels ? (
                <button
                  onClick={() => setShowAddModal(true)}
                  style={{
                    background: "#8b1634",
                    color: "#fff",
                    fontWeight: "600",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6d1128")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#8b1634")}
                >
                  <i className="fa fa-plus" /> Create Labels
                </button>
              ) : null}
            </div>
        </div>

        <div
          style={{
            overflowX: "auto",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "70px 2fr 1fr 1.3fr 1.2fr",
              background: "#f7fafc",
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>QR</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Label code</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Status</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Created at</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Actions</div>
          </div>

          <div>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #8b1634",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "20px",
                  }}
                />
                <div style={{ color: "#8b1634", fontSize: "16px", fontWeight: "600" }}>Loading QR labels...</div>
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ fontSize: "48px", color: "#e53e3e", marginBottom: "16px" }}>⚠️</div>
                <div style={{ color: "#e53e3e", fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>
                  Error Loading Data
                </div>
                <div
                  style={{
                    color: "#718096",
                    fontSize: "14px",
                    textAlign: "center",
                    maxWidth: "400px",
                    marginBottom: "20px",
                  }}
                >
                  {error}
                </div>
                <button
                  onClick={fetchLabels}
                  style={{
                    background: "#8b1634",
                    color: "#fff",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6d1128")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#8b1634")}
                >
                  <i className="fa fa-refresh" /> Try Again
                </button>
              </div>
            ) : paged.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ fontSize: "48px", color: "#cbd5e0", marginBottom: "16px" }}>🏷️</div>
                <div style={{ color: "#718096", fontSize: "16px", fontWeight: "600" }}>No QR labels found</div>
                {search && (
                  <div style={{ color: "#a0aec0", fontSize: "14px", marginTop: "8px" }}>
                    Try adjusting your search criteria
                  </div>
                )}
              </div>
            ) : (
              <>
                {paged.map((row, idx) => (
                  <div
                    key={row._id || idx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "70px 2fr 1fr 1.3fr 1.2fr",
                      padding: "10px 16px",
                      borderBottom: "1px solid #f1f5f9",
                      gap: "16px",
                      alignItems: "center",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <LabelQrThumb labelCode={row.labelCode} />
                    </div>
                    <div
                      style={{
                        color: "#2d3748",
                        fontSize: "13px",
                        fontFamily: "ui-monospace, monospace",
                        wordBreak: "break-all",
                      }}
                    >
                      {row.labelCode || "-"}
                    </div>
                    <div style={{ color: "#2d3748", fontSize: "14px", textTransform: "capitalize" }}>
                      {row.status || "-"}
                    </div>
                    <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatCreatedAt(row.createdAt)}</div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <button
                        onClick={() => handleDownloadPdf(row)}
                        title="Download PDF"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "6px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#eff6ff")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <i className="fa fa-file-pdf-o" style={{ color: "#2563eb", fontSize: "16px" }} />
                      </button>
                      <button
                        onClick={() => handleDelete(row._id)}
                        title={row.status === "unassigned" ? "Delete" : "Only unassigned labels can be deleted"}
                        disabled={row.status !== "unassigned"}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: row.status === "unassigned" ? "pointer" : "not-allowed",
                          padding: "6px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: row.status === "unassigned" ? 1 : 0.35,
                        }}
                        onMouseEnter={(e) => {
                          if (row.status === "unassigned") e.currentTarget.style.backgroundColor = "#fef2f2";
                        }}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: "16px" }} />
                      </button>
                    </div>
                  </div>
                ))}
                {Array.from({ length: emptyRowsCount }).map((_, idx) => (
                  <div
                    key={`empty-${idx}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "70px 2fr 1fr 1.3fr 1.2fr",
                      padding: "10px 16px",
                      borderBottom: "1px solid #f1f5f9",
                      gap: "16px",
                      alignItems: "center",
                      minHeight: "70px",
                    }}
                  >
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {showAddModal && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px", width: "calc(100% - 2rem)" }}>
              <div className="modal-content border-0 shadow" style={{ borderRadius: "14px", overflow: "hidden" }}>
                <div
                  className="modal-header border-0 py-3"
                  style={{
                    background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)",
                    color: "#fff",
                  }}
                >
                  <h5 className="modal-title" style={{ fontWeight: 700, fontSize: "17px" }}>
                    Create labels
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={closeAddModal} aria-label="Close" />
                </div>
                <div className="modal-body pt-4 pb-3 px-4">
                  <label className="form-label mb-2" style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>
                    Quantity
                  </label>
                  <div className="input-group">
                    <span
                      className="input-group-text border-end-0"
                      style={{ background: "#faf5f7", color: "#8b1634", borderColor: "#edd5df" }}
                    >
                      <i className="fa fa-hashtag" />
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="form-control border-start-0"
                      style={{ borderColor: "#edd5df", minHeight: "44px" }}
                      autoComplete="off"
                      value={newBatchQuantity}
                      onChange={handleQuantityChange}
                      placeholder="e.g. 20"
                      onFocus={(e) => {
                        e.target.style.borderColor = "#8b1634";
                        e.target.style.boxShadow = "0 0 0 0.15rem rgba(139, 22, 52, 0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#edd5df";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div className="text-muted small mt-2">1–{MAX_QTY}</div>

                  <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "14px", background: "#fbeef2", border: "1px solid #edd5df", borderRadius: "8px", padding: "10px 12px" }}>
                    <i className="fa-solid fa-print" style={{ color: "#8b1634", marginTop: "3px" }} />
                    <span style={{ fontSize: "12px", color: "#4a5568", lineHeight: 1.5 }}>
                      Labels are generated in one PDF, <b>one label per page (25 × 25 mm)</b>, ready to print on the <b>Zebra ZD421</b> label printer.
                    </span>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0 pb-3 px-4 gap-2" style={{ background: "#fafafa" }}>
                  <button
                    type="button"
                    className="btn btn-light border"
                    style={{ color: "#4a5568", fontWeight: 600 }}
                    onClick={closeAddModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{
                      background: "linear-gradient(135deg, #8b1634, #b42352)",
                      color: "#fff",
                      fontWeight: 600,
                      border: "none",
                    }}
                    onClick={handlePrintLabels}
                  >
                    Create Labels
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "400px", width: "calc(100% - 2rem)" }}>
              <div className="modal-content border-0 shadow" style={{ borderRadius: "14px", overflow: "hidden" }}>
                <div
                  className="modal-header border-0 py-3"
                  style={{
                    background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)",
                    color: "#fff",
                  }}
                >
                  <h5 className="modal-title" style={{ fontWeight: 700, fontSize: "17px" }}>
                    Delete QR label
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowDeleteModal(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body px-4 pt-3 pb-2">
                  <p className="mb-0" style={{ color: "#4a5568" }}>
                    Are you sure you want to delete this label?
                  </p>
                </div>
                <div className="modal-footer border-0 pt-0 pb-3 px-4 gap-2" style={{ background: "#fafafa" }}>
                  <button
                    className="btn btn-light border"
                    style={{ color: "#4a5568", fontWeight: 600 }}
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger fw-semibold" onClick={confirmDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "18px",
              gap: "12px",
            }}
          >
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: page === 1 ? "#f7fafc" : "#ffffff",
                color: page === 1 ? "#a0aec0" : "#8b1634",
                cursor: page === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Prev
            </button>
            <span style={{ fontWeight: "700", color: "#8b1634", fontSize: "16px" }}>
              {page} / {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: page === totalPages ? "#f7fafc" : "#ffffff",
                color: page === totalPages ? "#a0aec0" : "#8b1634",
                cursor: page === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default AssetQrLabelsPage;
